import initSqlJs, { Database } from 'sql.js';
import {
  IDatabaseService,
  Deck,
  Card,
  Session,
  CardPerformance,
  UserCardPreference,
  DeckWithCount,
  SessionSummary,
  CardAttemptStats,
  SessionCardStats,
  CardAttempt,
  ExportData,
  ImportResult,
  ClearProgressResult
} from './IDatabaseService';

/**
 * Web database adapter using sql.js (SQLite compiled to WebAssembly)
 * Data is persisted to IndexedDB after every write operation
 */
export class WebDatabaseAdapter implements IDatabaseService {
  private db: Database | null = null;
  private SQL: any = null;
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('[WebDatabase] Initializing sql.js...');

      // Load sql.js WebAssembly
      this.SQL = await initSqlJs({
        locateFile: file => `https://sql.js.org/dist/${file}`
      });

      // Try to load existing database from IndexedDB
      const savedDb = await this.loadFromIndexedDB();

      if (savedDb) {
        console.log('[WebDatabase] Loaded existing database from IndexedDB');
        this.db = new this.SQL.Database(savedDb);
        // Run migrations on existing database
        this.runMigrations();
      } else {
        console.log('[WebDatabase] Creating new database');
        this.db = new this.SQL.Database();
        this.createTables();

        // Load starter decks on first initialization
        await this.loadStarterData();
      }

      this.isInitialized = true;
      console.log('[WebDatabase] Initialization complete');
    } catch (error) {
      console.error('[WebDatabase] Initialization failed:', error);
      throw error;
    }
  }

  private async loadFromIndexedDB(): Promise<Uint8Array | null> {
    return new Promise((resolve) => {
      const request = indexedDB.open('FlashE', 1);

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('database')) {
          resolve(null);
          return;
        }

        const transaction = db.transaction(['database'], 'readonly');
        const store = transaction.objectStore('database');
        const getRequest = store.get('main');

        getRequest.onsuccess = () => {
          resolve(getRequest.result?.data || null);
        };

        getRequest.onerror = () => resolve(null);
      };

      request.onerror = () => resolve(null);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('database')) {
          db.createObjectStore('database');
        }
      };
    });
  }

  private async saveToIndexedDB(): Promise<void> {
    if (!this.db) return;

    const data = this.db.export();

    return new Promise((resolve, reject) => {
      const request = indexedDB.open('FlashE', 1);

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(['database'], 'readwrite');
        const store = transaction.objectStore('database');

        store.put({ data }, 'main');

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(new Error('Failed to save to IndexedDB'));
      };

      request.onerror = () => reject(new Error('Failed to open IndexedDB'));
    });
  }

  /**
   * Fetch starter decks with cache-busting using manifest hash
   * First fetches the manifest with a timestamp to bust cache, then uses the content hash
   */
  private async fetchStarterDecksWithCacheBusting(): Promise<{ decks: unknown[] } | null> {
    try {
      // Fetch manifest with timestamp to ensure we get the latest version
      const timestamp = Date.now();
      const manifestResponse = await fetch(`/starter-decks-manifest.json?t=${timestamp}`);
      
      let contentHash = '';
      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        contentHash = manifest.contentHash || '';
        console.log(`[WebDatabase] Manifest loaded, content hash: ${contentHash}`);
      } else {
        console.log('[WebDatabase] Manifest not found, falling back to timestamp-based cache-busting');
      }

      // Fetch starter decks with hash (or timestamp if no manifest)
      const cacheParam = contentHash ? `h=${contentHash}` : `t=${timestamp}`;
      const response = await fetch(`/starter-decks.json?${cacheParam}`);

      if (!response.ok) {
        console.warn(`[WebDatabase] Starter decks fetch failed with status ${response.status}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.warn('[WebDatabase] Failed to fetch starter decks:', error);
      if (!navigator.onLine) {
        throw new Error('You appear to be offline. Please check your connection and try again.');
      }
      throw new Error('Could not load starter decks. Please check your connection and try again.');
    }
  }

  private createTables(): void {
    if (!this.db) return;

    // Decks table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS decks (
        id TEXT PRIMARY KEY,
        name TEXT NOT NULL,
        description TEXT,
        created_at TEXT NOT NULL,
        modified_at TEXT NOT NULL,
        category_tags TEXT,
        difficulty TEXT CHECK(difficulty IN ('Easy', 'Medium', 'Hard')),
        target_languages TEXT,
        source TEXT DEFAULT 'user',
        dirty INTEGER DEFAULT 0
      )
    `);

    // Cards table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS cards (
        id TEXT PRIMARY KEY,
        deck_id TEXT NOT NULL,
        name TEXT NOT NULL,
        question TEXT NOT NULL,
        category TEXT NOT NULL,
        difficulty TEXT CHECK(difficulty IN ('Easy', 'Medium', 'Hard')),
        languages TEXT NOT NULL,
        notes TEXT,
        tags TEXT,
        media TEXT,
        created_at TEXT NOT NULL,
        modified_at TEXT NOT NULL,
        display_order INTEGER DEFAULT 0,
        FOREIGN KEY (deck_id) REFERENCES decks (id) ON DELETE CASCADE
      )
    `);

    // Sessions table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS sessions (
        id TEXT PRIMARY KEY,
        deck_ids TEXT NOT NULL,
        start_time TEXT NOT NULL,
        end_time TEXT,
        cards_attempted INTEGER DEFAULT 0,
        cards_correct INTEGER DEFAULT 0,
        duration INTEGER,
        mode TEXT CHECK(mode IN ('sequential', 'reverse_sequential', 'random', 'failed', 'never_correct', 'adaptive'))
      )
    `);

    // Card performance table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS card_performance (
        id TEXT PRIMARY KEY,
        card_id TEXT NOT NULL,
        session_id TEXT NOT NULL,
        is_correct BOOLEAN NOT NULL,
        response_time INTEGER,
        timestamp TEXT NOT NULL,
        ease_factor REAL DEFAULT 2.5,
        interval INTEGER DEFAULT 1,
        repetitions INTEGER DEFAULT 0,
        ai_evaluation TEXT,
        manual_override BOOLEAN DEFAULT 0,
        user_answer TEXT,
        FOREIGN KEY (card_id) REFERENCES cards (id) ON DELETE CASCADE,
        FOREIGN KEY (session_id) REFERENCES sessions (id) ON DELETE CASCADE
      )
    `);

    // User card preferences table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS user_card_preferences (
        id TEXT PRIMARY KEY,
        card_id TEXT NOT NULL,
        deck_id TEXT NOT NULL,
        is_excluded BOOLEAN DEFAULT 0,
        created_at TEXT NOT NULL,
        modified_at TEXT NOT NULL,
        FOREIGN KEY (card_id) REFERENCES cards (id) ON DELETE CASCADE,
        FOREIGN KEY (deck_id) REFERENCES decks (id) ON DELETE CASCADE,
        UNIQUE(card_id, deck_id)
      )
    `);

    // Create indexes
    this.db.run(`CREATE INDEX IF NOT EXISTS idx_cards_deck_id ON cards (deck_id)`);
    this.db.run(`CREATE INDEX IF NOT EXISTS idx_performance_card_id ON card_performance (card_id)`);
    this.db.run(`CREATE INDEX IF NOT EXISTS idx_performance_session_id ON card_performance (session_id)`);
    this.db.run(`CREATE INDEX IF NOT EXISTS idx_user_card_preferences_deck_id ON user_card_preferences (deck_id)`);
    this.db.run(`CREATE INDEX IF NOT EXISTS idx_user_card_preferences_card_id ON user_card_preferences (card_id)`);
  }

  /**
   * Run database migrations for existing databases
   * This handles schema changes that can't be applied via CREATE TABLE IF NOT EXISTS
   */
  private runMigrations(): void {
    if (!this.db) return;

    console.log('[WebDatabase] Running migrations...');

    // Migration 1: Update sessions table CHECK constraint to include 'reverse_sequential'
    // SQLite doesn't support ALTER TABLE to modify constraints, so we recreate the table
    try {
      // Check if the sessions table exists and needs migration
      const tableInfo = this.db.exec(`SELECT sql FROM sqlite_master WHERE type='table' AND name='sessions'`);

      if (tableInfo.length > 0 && tableInfo[0].values.length > 0) {
        const createSql = tableInfo[0].values[0][0] as string;

        // Check if 'reverse_sequential' is missing from the constraint
        if (createSql && !createSql.includes('reverse_sequential')) {
          console.log('[WebDatabase] Migrating sessions table to add reverse_sequential mode...');

          // Create new table with updated constraint
          this.db.run(`
            CREATE TABLE sessions_new (
              id TEXT PRIMARY KEY,
              deck_ids TEXT NOT NULL,
              start_time TEXT NOT NULL,
              end_time TEXT,
              cards_attempted INTEGER DEFAULT 0,
              cards_correct INTEGER DEFAULT 0,
              duration INTEGER,
              mode TEXT CHECK(mode IN ('sequential', 'reverse_sequential', 'random', 'failed', 'never_correct', 'adaptive'))
            )
          `);

          // Copy data from old table
          this.db.run(`
            INSERT INTO sessions_new (id, deck_ids, start_time, end_time, cards_attempted, cards_correct, duration, mode)
            SELECT id, deck_ids, start_time, end_time, cards_attempted, cards_correct, duration, mode
            FROM sessions
          `);

          // Drop old table and rename new one
          this.db.run(`DROP TABLE sessions`);
          this.db.run(`ALTER TABLE sessions_new RENAME TO sessions`);

          console.log('[WebDatabase] Sessions table migration complete');
        }
      }
    } catch (error) {
      console.error('[WebDatabase] Migration failed:', error);
      // Don't throw - allow app to continue with potential issues
    }

    // Migration 2: Add source and dirty columns to decks table
    try {
      this.db.run(`ALTER TABLE decks ADD COLUMN source TEXT DEFAULT 'user'`);
      console.log('[WebDatabase] Added source column to decks table');
    } catch {
      // Column already exists
    }
    try {
      this.db.run(`ALTER TABLE decks ADD COLUMN dirty INTEGER DEFAULT 0`);
      console.log('[WebDatabase] Added dirty column to decks table');
    } catch {
      // Column already exists
    }

    console.log('[WebDatabase] Migrations complete');
  }

  /**
   * Load starter decks from bundled JSON file
   * Only called on first initialization (new database)
   * Silently fails if starter-decks.json is not available
   */
  private async loadStarterData(): Promise<void> {
    try {
      console.log('[WebDatabase] Loading starter decks...');

      // Fetch bundled starter decks with cache-busting
      const starterData = await this.fetchStarterDecksWithCacheBusting();

      if (!starterData) {
        console.warn('[WebDatabase] Starter decks file not found, skipping');
        return;
      }

      if (!starterData.decks || !Array.isArray(starterData.decks)) {
        console.warn('[WebDatabase] Invalid starter decks format, skipping');
        return;
      }

      console.log(`[WebDatabase] Importing ${starterData.decks.length} starter decks...`);

      let importedDecks = 0;
      let importedCards = 0;

      // Import each deck and its cards
      for (const deckData of starterData.decks as Record<string, unknown>[]) {
        if (!deckData.id || !deckData.name) {
          console.warn('[WebDatabase] Skipping deck with missing id or name');
          continue;
        }

        // Insert deck with source = 'starter'
        await this.run(
          `INSERT INTO decks (id, name, description, created_at, modified_at, category_tags, difficulty, target_languages, source, dirty)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'starter', 0)`,
          [
            deckData.id,
            deckData.name,
            deckData.description || '',
            deckData.createdAt || new Date().toISOString(),
            deckData.modifiedAt || new Date().toISOString(),
            JSON.stringify(deckData.categoryTags || []),
            deckData.difficulty || 'Medium',
            JSON.stringify(deckData.targetLanguages || [])
          ]
        );
        importedDecks++;

        // Insert cards for this deck
        const cards = deckData.cards as Record<string, unknown>[] | undefined;
        if (cards && Array.isArray(cards)) {
          for (const cardData of cards) {
            if (!cardData.id || !cardData.deckId) {
              console.warn('[WebDatabase] Skipping card with missing id or deckId');
              continue;
            }

            await this.run(
              `INSERT INTO cards (id, deck_id, name, question, category, difficulty, languages, notes, tags, media, created_at, modified_at, display_order)
               VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
              [
                cardData.id,
                cardData.deckId,
                cardData.name || 'Untitled Card',
                cardData.question || '',
                cardData.category || 'General',
                cardData.difficulty || 'Medium',
                JSON.stringify(cardData.languages || {}),
                cardData.notes || '',
                JSON.stringify(cardData.tags || []),
                JSON.stringify(cardData.media || []),
                cardData.createdAt || new Date().toISOString(),
                cardData.modifiedAt || new Date().toISOString(),
                cardData.displayOrder || 0
              ]
            );
            importedCards++;
          }
        }
      }

      console.log(`[WebDatabase] Starter data loaded: ${importedDecks} decks, ${importedCards} cards`);
    } catch (error) {
      // Silently fail - starter data is optional
      console.warn('[WebDatabase] Failed to load starter data:', error);
    }
  }


  /**
   * Refresh starter decks - import new decks from starter-decks.json
   * that don't already exist in the database
   */
  async refreshStarterDecks(): Promise<{ success: boolean; imported: number; skipped: number; orphansRemoved?: number; dirtyDecks?: { id: string; name: string }[]; error?: string }> {
    try {
      console.log('[WebDatabase] Refreshing starter decks...');

      // Fetch bundled starter decks with cache-busting
      const starterData = await this.fetchStarterDecksWithCacheBusting();

      if (!starterData) {
        return {
          success: false,
          imported: 0,
          skipped: 0,
          error: 'Starter decks file not found'
        };
      }

      if (!starterData.decks || !Array.isArray(starterData.decks)) {
        return {
          success: false,
          imported: 0,
          skipped: 0,
          error: 'Invalid starter decks format'
        };
      }

      // Get starter deck IDs from the bundle
      const bundleDeckIds = new Set(
        (starterData.decks as Record<string, unknown>[])
          .filter(d => d.id)
          .map(d => d.id as string)
      );

      // Find dirty starter decks that exist in the bundle — skip these
      const dirtyDecks: { id: string; name: string }[] = [];
      const dirtyResult = this.db!.exec(
        `SELECT id, name FROM decks WHERE source = 'starter' AND dirty = 1`
      );
      if (dirtyResult.length > 0) {
        for (const row of dirtyResult[0].values) {
          const deckId = row[0] as string;
          const deckName = row[1] as string;
          if (bundleDeckIds.has(deckId)) {
            dirtyDecks.push({ id: deckId, name: deckName });
          }
        }
      }
      const dirtyDeckIds = new Set(dirtyDecks.map(d => d.id));

      // Delete and re-import non-dirty starter decks that are in the bundle
      let importedCount = 0;
      let skippedCount = 0;

      for (const deckData of starterData.decks as Record<string, unknown>[]) {
        if (!deckData.id || !deckData.name) {
          console.warn('[WebDatabase] Skipping deck with missing id or name');
          skippedCount++;
          continue;
        }

        const deckId = deckData.id as string;

        // Skip dirty decks — user has edited them
        if (dirtyDeckIds.has(deckId)) {
          console.log(`[WebDatabase] Skipping dirty deck: ${deckData.name}`);
          skippedCount++;
          continue;
        }

        // Delete existing deck and cards, then re-import
        await this.run('DELETE FROM cards WHERE deck_id = ?', [deckId]);
        await this.run('DELETE FROM decks WHERE id = ?', [deckId]);

        // Insert deck with source = 'starter'
        await this.run(
          `INSERT INTO decks (id, name, description, created_at, modified_at, category_tags, difficulty, target_languages, source, dirty)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'starter', 0)`,
          [
            deckId,
            deckData.name,
            deckData.description || '',
            deckData.createdAt || new Date().toISOString(),
            deckData.modifiedAt || new Date().toISOString(),
            JSON.stringify(deckData.categoryTags || []),
            deckData.difficulty || 'Medium',
            JSON.stringify(deckData.targetLanguages || [])
          ]
        );

        // Insert cards for this deck
        const cards = deckData.cards as Record<string, unknown>[] | undefined;
        if (cards && Array.isArray(cards)) {
          for (const cardData of cards) {
            if (!cardData.id || !cardData.deckId) {
              console.warn('[WebDatabase] Skipping card with missing id or deckId');
              continue;
            }

            // Remove any existing card with this ID (e.g. from a renamed/split deck)
            await this.run('DELETE FROM cards WHERE id = ?', [cardData.id]);
            await this.run(
              `INSERT INTO cards (id, deck_id, name, question, category, difficulty, languages, notes, tags, media, created_at, modified_at, display_order)
               VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
              [
                cardData.id,
                cardData.deckId,
                cardData.name || 'Untitled Card',
                cardData.question || '',
                cardData.category || 'General',
                cardData.difficulty || 'Medium',
                JSON.stringify(cardData.languages || {}),
                cardData.notes || '',
                JSON.stringify(cardData.tags || []),
                JSON.stringify(cardData.media || []),
                cardData.createdAt || new Date().toISOString(),
                cardData.modifiedAt || new Date().toISOString(),
                cardData.displayOrder || 0
              ]
            );
          }
        }

        importedCount++;
        console.log(`[WebDatabase] Imported deck: ${deckData.name} (${(cards || []).length} cards)`);
      }

      // Remove orphaned starter decks (source='starter', not in bundle, not dirty)
      let orphansRemoved = 0;
      const orphanResult = this.db!.exec(
        `SELECT id, name FROM decks WHERE source = 'starter' AND dirty = 0`
      );
      if (orphanResult.length > 0) {
        for (const row of orphanResult[0].values) {
          const orphanId = row[0] as string;
          const orphanName = row[1] as string;
          if (!bundleDeckIds.has(orphanId)) {
            console.log(`[WebDatabase] Removing orphaned starter deck: ${orphanName} (${orphanId})`);
            await this.run('DELETE FROM cards WHERE deck_id = ?', [orphanId]);
            await this.run('DELETE FROM decks WHERE id = ?', [orphanId]);
            orphansRemoved++;
          }
        }
      }

      await this.saveToIndexedDB();

      console.log(`[WebDatabase] Refresh complete: ${importedCount} imported, ${skippedCount} skipped, ${orphansRemoved} orphans removed`);

      return {
        success: true,
        imported: importedCount,
        skipped: skippedCount,
        orphansRemoved,
        dirtyDecks: dirtyDecks.length > 0 ? dirtyDecks : undefined
      };
    } catch (error) {
      console.error('[WebDatabase] Failed to refresh starter decks:', error);
      return {
        success: false,
        imported: 0,
        skipped: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async overwriteDirtyDecks(deckIds: string[]): Promise<{ success: boolean; overwritten: number; error?: string }> {
    try {
      console.log(`[WebDatabase] Overwriting ${deckIds.length} dirty deck(s)...`);

      const starterData = await this.fetchStarterDecksWithCacheBusting();
      if (!starterData || !starterData.decks || !Array.isArray(starterData.decks)) {
        return { success: false, overwritten: 0, error: 'Starter decks file not found' };
      }

      const deckIdSet = new Set(deckIds);
      let overwritten = 0;

      for (const deckData of starterData.decks as Record<string, unknown>[]) {
        if (!deckData.id || !deckIdSet.has(deckData.id as string)) continue;

        const deckId = deckData.id as string;

        // Delete existing deck and cards
        await this.run('DELETE FROM cards WHERE deck_id = ?', [deckId]);
        await this.run('DELETE FROM decks WHERE id = ?', [deckId]);

        // Re-insert deck with source='starter', dirty=0
        await this.run(
          `INSERT INTO decks (id, name, description, created_at, modified_at, category_tags, difficulty, target_languages, source, dirty)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'starter', 0)`,
          [
            deckId,
            deckData.name,
            deckData.description || '',
            deckData.createdAt || new Date().toISOString(),
            deckData.modifiedAt || new Date().toISOString(),
            JSON.stringify(deckData.categoryTags || []),
            deckData.difficulty || 'Medium',
            JSON.stringify(deckData.targetLanguages || [])
          ]
        );

        // Insert cards
        const cards = deckData.cards as Record<string, unknown>[] | undefined;
        if (cards && Array.isArray(cards)) {
          for (const cardData of cards) {
            if (!cardData.id || !cardData.deckId) continue;
            await this.run('DELETE FROM cards WHERE id = ?', [cardData.id]);
            await this.run(
              `INSERT INTO cards (id, deck_id, name, question, category, difficulty, languages, notes, tags, media, created_at, modified_at, display_order)
               VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
              [
                cardData.id,
                cardData.deckId,
                cardData.name || 'Untitled Card',
                cardData.question || '',
                cardData.category || 'General',
                cardData.difficulty || 'Medium',
                JSON.stringify(cardData.languages || {}),
                cardData.notes || '',
                JSON.stringify(cardData.tags || []),
                JSON.stringify(cardData.media || []),
                cardData.createdAt || new Date().toISOString(),
                cardData.modifiedAt || new Date().toISOString(),
                cardData.displayOrder || 0
              ]
            );
          }
        }

        overwritten++;
        console.log(`[WebDatabase] Overwrote dirty deck: ${deckData.name}`);
      }

      await this.saveToIndexedDB();
      return { success: true, overwritten };
    } catch (error) {
      console.error('[WebDatabase] Failed to overwrite dirty decks:', error);
      return {
        success: false,
        overwritten: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Helper to execute SELECT query and get results
  private query<T>(sql: string, params: any[] = []): T[] {
    if (!this.db) throw new Error('Database not initialized');

    const stmt = this.db.prepare(sql);
    if (params.length > 0) {
      stmt.bind(params);
    }

    const results: T[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push(row as T);
    }
    stmt.free();

    return results;
  }

  // Helper to execute INSERT/UPDATE/DELETE and save to IndexedDB
  private async run(sql: string, params: any[] = []): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    this.db.run(sql, params);
    await this.saveToIndexedDB();
  }

  private generateId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // ==================== DECK OPERATIONS ====================

  async getDecks(): Promise<Deck[]> {
    const rows = this.query<any>('SELECT * FROM decks ORDER BY modified_at DESC');

    return rows.map(row => ({
      id: row.id,
      name: row.name,
      description: row.description || '',
      createdAt: row.created_at,
      modifiedAt: row.modified_at,
      categoryTags: JSON.parse(row.category_tags || '[]'),
      difficulty: row.difficulty as Deck['difficulty'],
      targetLanguages: JSON.parse(row.target_languages || '[]')
    }));
  }

  async getDeck(id: string): Promise<Deck | null> {
    const rows = this.query<any>('SELECT * FROM decks WHERE id = ?', [id]);
    if (rows.length === 0) return null;

    const row = rows[0];
    return {
      id: row.id,
      name: row.name,
      description: row.description || '',
      createdAt: row.created_at,
      modifiedAt: row.modified_at,
      categoryTags: JSON.parse(row.category_tags || '[]'),
      difficulty: row.difficulty as Deck['difficulty'],
      targetLanguages: JSON.parse(row.target_languages || '[]')
    };
  }

  async getDecksWithCardCounts(): Promise<DeckWithCount[]> {
    const rows = this.query<any>(`
      SELECT d.*, COUNT(c.id) as cardCount
      FROM decks d
      LEFT JOIN cards c ON d.id = c.deck_id
      GROUP BY d.id
      ORDER BY d.modified_at DESC
    `);

    return rows.map(row => ({
      id: row.id,
      name: row.name,
      description: row.description || '',
      createdAt: row.created_at,
      modifiedAt: row.modified_at,
      categoryTags: JSON.parse(row.category_tags || '[]'),
      difficulty: row.difficulty as Deck['difficulty'],
      targetLanguages: JSON.parse(row.target_languages || '[]'),
      cardCount: row.cardCount
    }));
  }

  async createDeck(deck: Omit<Deck, 'id'> & { id?: string }): Promise<Deck> {
    const id = deck.id || this.generateId();
    const now = new Date().toISOString();

    await this.run(
      `INSERT INTO decks (id, name, description, created_at, modified_at, category_tags, difficulty, target_languages)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        deck.name,
        deck.description || '',
        now,
        now,
        JSON.stringify(deck.categoryTags || []),
        deck.difficulty,
        JSON.stringify(deck.targetLanguages || [])
      ]
    );

    return {
      id,
      name: deck.name,
      description: deck.description || '',
      createdAt: now,
      modifiedAt: now,
      categoryTags: deck.categoryTags || [],
      difficulty: deck.difficulty,
      targetLanguages: deck.targetLanguages || []
    };
  }

  async updateDeck(id: string, updates: Partial<Deck>): Promise<Deck> {
    const now = new Date().toISOString();

    const setClauses: string[] = [];
    const params: any[] = [];

    if (updates.name !== undefined) {
      setClauses.push('name = ?');
      params.push(updates.name);
    }
    if (updates.description !== undefined) {
      setClauses.push('description = ?');
      params.push(updates.description);
    }
    if (updates.categoryTags !== undefined) {
      setClauses.push('category_tags = ?');
      params.push(JSON.stringify(updates.categoryTags));
    }
    if (updates.difficulty !== undefined) {
      setClauses.push('difficulty = ?');
      params.push(updates.difficulty);
    }
    if (updates.targetLanguages !== undefined) {
      setClauses.push('target_languages = ?');
      params.push(JSON.stringify(updates.targetLanguages));
    }

    setClauses.push('modified_at = ?');
    params.push(now);
    params.push(id);

    await this.run(
      `UPDATE decks SET ${setClauses.join(', ')} WHERE id = ?`,
      params
    );

    const updated = await this.getDeck(id);
    if (!updated) throw new Error(`Deck ${id} not found after update`);
    return updated;
  }

  async deleteDeck(id: string): Promise<void> {
    // Delete sessions referencing this deck (deck_ids is a JSON array, not a FK)
    await this.run("DELETE FROM sessions WHERE deck_ids LIKE '%' || ? || '%'", [id]);
    await this.run('DELETE FROM decks WHERE id = ?', [id]);
  }

  // ==================== CARD OPERATIONS ====================

  async getCards(deckId: string): Promise<Card[]> {
    const rows = this.query<any>(
      'SELECT * FROM cards WHERE deck_id = ? ORDER BY display_order ASC, created_at ASC',
      [deckId]
    );

    return rows.map(row => ({
      id: row.id,
      deckId: row.deck_id,
      name: row.name,
      question: row.question,
      category: row.category,
      difficulty: row.difficulty as Card['difficulty'],
      languages: JSON.parse(row.languages),
      notes: row.notes || '',
      tags: JSON.parse(row.tags || '[]'),
      media: JSON.parse(row.media || '[]'),
      createdAt: row.created_at,
      modifiedAt: row.modified_at,
      displayOrder: row.display_order
    }));
  }

  async getCard(id: string): Promise<Card | null> {
    const rows = this.query<any>('SELECT * FROM cards WHERE id = ?', [id]);
    if (rows.length === 0) return null;

    const row = rows[0];
    return {
      id: row.id,
      deckId: row.deck_id,
      name: row.name,
      question: row.question,
      category: row.category,
      difficulty: row.difficulty as Card['difficulty'],
      languages: JSON.parse(row.languages),
      notes: row.notes || '',
      tags: JSON.parse(row.tags || '[]'),
      media: JSON.parse(row.media || '[]'),
      createdAt: row.created_at,
      modifiedAt: row.modified_at,
      displayOrder: row.display_order
    };
  }

  async createCard(card: Card): Promise<Card> {
    const id = card.id || this.generateId();
    const now = new Date().toISOString();

    await this.run(
      `INSERT INTO cards (id, deck_id, name, question, category, difficulty, languages, notes, tags, media, created_at, modified_at, display_order)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        card.deckId,
        card.name,
        card.question,
        card.category,
        card.difficulty,
        JSON.stringify(card.languages),
        card.notes || '',
        JSON.stringify(card.tags || []),
        JSON.stringify(card.media || []),
        now,
        now,
        card.displayOrder || 0
      ]
    );

    return {
      ...card,
      id,
      createdAt: now,
      modifiedAt: now
    };
  }

  async updateCard(id: string, updates: Partial<Card>): Promise<Card> {
    const now = new Date().toISOString();

    const setClauses: string[] = [];
    const params: any[] = [];

    if (updates.name !== undefined) {
      setClauses.push('name = ?');
      params.push(updates.name);
    }
    if (updates.question !== undefined) {
      setClauses.push('question = ?');
      params.push(updates.question);
    }
    if (updates.category !== undefined) {
      setClauses.push('category = ?');
      params.push(updates.category);
    }
    if (updates.difficulty !== undefined) {
      setClauses.push('difficulty = ?');
      params.push(updates.difficulty);
    }
    if (updates.languages !== undefined) {
      setClauses.push('languages = ?');
      params.push(JSON.stringify(updates.languages));
    }
    if (updates.notes !== undefined) {
      setClauses.push('notes = ?');
      params.push(updates.notes);
    }
    if (updates.tags !== undefined) {
      setClauses.push('tags = ?');
      params.push(JSON.stringify(updates.tags));
    }
    if (updates.media !== undefined) {
      setClauses.push('media = ?');
      params.push(JSON.stringify(updates.media));
    }
    if (updates.displayOrder !== undefined) {
      setClauses.push('display_order = ?');
      params.push(updates.displayOrder);
    }

    setClauses.push('modified_at = ?');
    params.push(now);
    params.push(id);

    await this.run(
      `UPDATE cards SET ${setClauses.join(', ')} WHERE id = ?`,
      params
    );

    // Mark the parent deck as dirty if it's a starter deck
    await this.run(
      `UPDATE decks SET dirty = 1 WHERE id = (SELECT deck_id FROM cards WHERE id = ?) AND source = 'starter'`,
      [id]
    );

    const updated = await this.getCard(id);
    if (!updated) throw new Error(`Card ${id} not found after update`);
    return updated;
  }

  async deleteCard(id: string): Promise<void> {
    await this.run('DELETE FROM cards WHERE id = ?', [id]);
  }

  async updateCardOrder(deckId: string, cardIds: string[]): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    // Use transaction-like approach: prepare all updates, then save once
    const stmt = this.db.prepare('UPDATE cards SET display_order = ? WHERE id = ?');

    cardIds.forEach((cardId, index) => {
      stmt.run([index, cardId]);
    });

    stmt.free();
    await this.saveToIndexedDB();
  }

  // ==================== SESSION OPERATIONS ====================

  async getSessions(): Promise<Session[]> {
    const rows = this.query<any>('SELECT * FROM sessions ORDER BY start_time DESC');

    return rows.map(row => ({
      id: row.id,
      deckIds: JSON.parse(row.deck_ids),
      startTime: row.start_time,
      endTime: row.end_time,
      cardsAttempted: row.cards_attempted,
      cardsCorrect: row.cards_correct,
      duration: row.duration,
      mode: row.mode as Session['mode']
    }));
  }

  async createSession(session: Session): Promise<Session> {
    const id = session.id || this.generateId();

    await this.run(
      `INSERT INTO sessions (id, deck_ids, start_time, end_time, cards_attempted, cards_correct, duration, mode)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        JSON.stringify(session.deckIds),
        session.startTime,
        session.endTime || null,
        session.cardsAttempted || 0,
        session.cardsCorrect || 0,
        session.duration || null,
        session.mode
      ]
    );

    return {
      ...session,
      id
    };
  }

  async updateSession(id: string, updates: Partial<Session>): Promise<Session> {
    const setClauses: string[] = [];
    const params: any[] = [];

    if (updates.endTime !== undefined) {
      setClauses.push('end_time = ?');
      params.push(updates.endTime);
    }
    if (updates.cardsAttempted !== undefined) {
      setClauses.push('cards_attempted = ?');
      params.push(updates.cardsAttempted);
    }
    if (updates.cardsCorrect !== undefined) {
      setClauses.push('cards_correct = ?');
      params.push(updates.cardsCorrect);
    }
    if (updates.duration !== undefined) {
      setClauses.push('duration = ?');
      params.push(updates.duration);
    }

    params.push(id);

    await this.run(
      `UPDATE sessions SET ${setClauses.join(', ')} WHERE id = ?`,
      params
    );

    const rows = this.query<any>('SELECT * FROM sessions WHERE id = ?', [id]);
    if (rows.length === 0) throw new Error(`Session ${id} not found`);

    const row = rows[0];
    return {
      id: row.id,
      deckIds: JSON.parse(row.deck_ids),
      startTime: row.start_time,
      endTime: row.end_time,
      cardsAttempted: row.cards_attempted,
      cardsCorrect: row.cards_correct,
      duration: row.duration,
      mode: row.mode as Session['mode']
    };
  }

  async getSessionsWithSummary(): Promise<SessionSummary[]> {
    const rows = this.query<any>(`
      SELECT
        s.id as sessionId,
        s.start_time,
        s.end_time,
        s.cards_attempted,
        s.cards_correct,
        s.duration,
        d.name as deckName
      FROM sessions s
      LEFT JOIN decks d ON json_extract(s.deck_ids, '$[0]') = d.id
      ORDER BY s.start_time DESC
    `);

    return rows.map(row => ({
      sessionId: row.sessionId,
      deckName: row.deckName || 'Unknown',
      startTime: row.start_time,
      endTime: row.end_time,
      cardsAttempted: row.cards_attempted,
      percentageCorrect: row.cards_attempted > 0
        ? (row.cards_correct / row.cards_attempted) * 100
        : 0,
      duration: row.duration
    }));
  }

  async getSessionSummary(sessionId: string): Promise<SessionSummary | null> {
    const rows = this.query<any>(`
      SELECT
        s.id as sessionId,
        s.start_time,
        s.end_time,
        s.cards_attempted,
        s.cards_correct,
        s.duration,
        d.name as deckName
      FROM sessions s
      LEFT JOIN decks d ON json_extract(s.deck_ids, '$[0]') = d.id
      WHERE s.id = ?
    `, [sessionId]);

    if (rows.length === 0) return null;

    const row = rows[0];
    return {
      sessionId: row.sessionId,
      deckName: row.deckName || 'Unknown',
      startTime: row.start_time,
      endTime: row.end_time,
      cardsAttempted: row.cards_attempted,
      percentageCorrect: row.cards_attempted > 0
        ? (row.cards_correct / row.cards_attempted) * 100
        : 0,
      duration: row.duration
    };
  }

  // ==================== PERFORMANCE TRACKING ====================

  async getCardPerformance(cardId: string): Promise<CardPerformance[]> {
    const rows = this.query<any>(
      'SELECT * FROM card_performance WHERE card_id = ? ORDER BY timestamp DESC',
      [cardId]
    );

    return rows.map(row => ({
      id: row.id,
      cardId: row.card_id,
      sessionId: row.session_id,
      isCorrect: Boolean(row.is_correct),
      responseTime: row.response_time,
      timestamp: row.timestamp,
      easeFactor: row.ease_factor,
      interval: row.interval,
      repetitions: row.repetitions,
      aiEvaluation: row.ai_evaluation,
      manualOverride: Boolean(row.manual_override),
      userAnswer: row.user_answer
    }));
  }

  async createPerformanceRecord(performance: Omit<CardPerformance, 'id'>): Promise<CardPerformance> {
    const id = this.generateId();

    await this.run(
      `INSERT INTO card_performance (id, card_id, session_id, is_correct, response_time, timestamp, ease_factor, interval, repetitions, ai_evaluation, manual_override, user_answer)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        performance.cardId,
        performance.sessionId,
        performance.isCorrect ? 1 : 0,
        performance.responseTime || null,
        performance.timestamp,
        performance.easeFactor || 2.5,
        performance.interval || 1,
        performance.repetitions || 0,
        performance.aiEvaluation || null,
        performance.manualOverride ? 1 : 0,
        performance.userAnswer || null
      ]
    );

    return {
      id,
      ...performance
    };
  }

  async getCardAttemptStats(cardId: string): Promise<CardAttemptStats> {
    const rows = this.query<any>(`
      SELECT
        COUNT(*) as totalAttempts,
        SUM(CASE WHEN is_correct = 1 THEN 1 ELSE 0 END) as correctAttempts
      FROM card_performance
      WHERE card_id = ?
    `, [cardId]);

    const row = rows[0];
    const totalAttempts = row.totalAttempts || 0;
    const correctAttempts = row.correctAttempts || 0;

    return {
      totalAttempts,
      correctAttempts,
      percentageCorrect: totalAttempts > 0 ? (correctAttempts / totalAttempts) * 100 : 0
    };
  }

  async getSessionCardStats(cardId: string, sessionId: string): Promise<SessionCardStats> {
    const rows = this.query<any>(`
      SELECT
        COUNT(*) as sessionAttempts,
        SUM(CASE WHEN is_correct = 1 THEN 1 ELSE 0 END) as sessionCorrect
      FROM card_performance
      WHERE card_id = ? AND session_id = ?
    `, [cardId, sessionId]);

    const row = rows[0];
    const sessionAttempts = row.sessionAttempts || 0;
    const sessionCorrect = row.sessionCorrect || 0;

    return {
      sessionAttempts,
      sessionCorrect,
      sessionPercentageCorrect: sessionAttempts > 0 ? (sessionCorrect / sessionAttempts) * 100 : 0
    };
  }

  async getSessionCardAttempts(sessionId: string): Promise<CardAttempt[]> {
    const rows = this.query<any>(`
      SELECT
        cp.card_id,
        c.name as cardName,
        cp.timestamp,
        cp.is_correct,
        cp.user_answer
      FROM card_performance cp
      JOIN cards c ON cp.card_id = c.id
      WHERE cp.session_id = ?
      ORDER BY cp.timestamp ASC
    `, [sessionId]);

    return rows.map(row => ({
      cardId: row.card_id,
      cardName: row.cardName,
      timestamp: row.timestamp,
      isCorrect: Boolean(row.is_correct),
      userAnswer: row.user_answer
    }));
  }

  // ==================== USER PREFERENCES ====================

  async getUserCardPreferences(deckId: string): Promise<UserCardPreference[]> {
    const rows = this.query<any>(
      'SELECT * FROM user_card_preferences WHERE deck_id = ?',
      [deckId]
    );

    return rows.map(row => ({
      id: row.id,
      cardId: row.card_id,
      deckId: row.deck_id,
      isExcluded: Boolean(row.is_excluded),
      createdAt: row.created_at,
      modifiedAt: row.modified_at
    }));
  }

  async getCardPreference(cardId: string, deckId: string): Promise<UserCardPreference | null> {
    const rows = this.query<any>(
      'SELECT * FROM user_card_preferences WHERE card_id = ? AND deck_id = ?',
      [cardId, deckId]
    );

    if (rows.length === 0) return null;

    const row = rows[0];
    return {
      id: row.id,
      cardId: row.card_id,
      deckId: row.deck_id,
      isExcluded: Boolean(row.is_excluded),
      createdAt: row.created_at,
      modifiedAt: row.modified_at
    };
  }

  async updateCardPreference(cardId: string, deckId: string, isExcluded: boolean): Promise<UserCardPreference> {
    const existing = await this.getCardPreference(cardId, deckId);

    if (existing) {
      const now = new Date().toISOString();
      await this.run(
        'UPDATE user_card_preferences SET is_excluded = ?, modified_at = ? WHERE card_id = ? AND deck_id = ?',
        [isExcluded ? 1 : 0, now, cardId, deckId]
      );

      return {
        ...existing,
        isExcluded,
        modifiedAt: now
      };
    } else {
      const id = this.generateId();
      const now = new Date().toISOString();

      await this.run(
        `INSERT INTO user_card_preferences (id, card_id, deck_id, is_excluded, created_at, modified_at)
         VALUES (?, ?, ?, ?, ?, ?)`,
        [id, cardId, deckId, isExcluded ? 1 : 0, now, now]
      );

      return {
        id,
        cardId,
        deckId,
        isExcluded,
        createdAt: now,
        modifiedAt: now
      };
    }
  }

  // ==================== IMPORT/EXPORT ====================

  async exportData(): Promise<ExportData> {
    const decks = await this.getDecks();

    const allCards: Card[] = [];
    for (const deck of decks) {
      const cards = await this.getCards(deck.id);
      allCards.push(...cards);
    }

    const sessions = await this.getSessions();

    const performanceRows = this.query<any>('SELECT * FROM card_performance');
    const performance: CardPerformance[] = performanceRows.map(row => ({
      id: row.id,
      cardId: row.card_id,
      sessionId: row.session_id,
      isCorrect: Boolean(row.is_correct),
      responseTime: row.response_time,
      timestamp: row.timestamp,
      easeFactor: row.ease_factor,
      interval: row.interval,
      repetitions: row.repetitions,
      aiEvaluation: row.ai_evaluation,
      manualOverride: Boolean(row.manual_override),
      userAnswer: row.user_answer
    }));

    return {
      exportDate: new Date().toISOString(),
      version: '1.0.0',
      data: {
        decks,
        cards: allCards,
        sessions,
        performance
      }
    };
  }

  async exportSelectedDecks(deckIds: string[]): Promise<ExportData> {
    const decks: Deck[] = [];
    const cards: Card[] = [];

    for (const deckId of deckIds) {
      const deck = await this.getDeck(deckId);
      if (deck) {
        decks.push(deck);
        const deckCards = await this.getCards(deckId);
        cards.push(...deckCards);
      }
    }

    return {
      exportDate: new Date().toISOString(),
      version: '1.0.0',
      data: {
        decks,
        cards,
        sessions: [],
        performance: []
      }
    };
  }

  async importData(data: ExportData): Promise<ImportResult> {
    try {
      if (!this.db) throw new Error('Database not initialized');

      // Import decks
      for (const deck of data.data.decks) {
        await this.createDeck(deck);
      }

      // Import cards
      for (const card of data.data.cards) {
        await this.createCard(card);
      }

      // Import sessions (if any)
      for (const session of data.data.sessions || []) {
        await this.createSession(session);
      }

      // Import performance (if any)
      for (const perf of data.data.performance || []) {
        await this.createPerformanceRecord(perf);
      }

      return {
        success: true,
        deckId: data.data.decks[0]?.id
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Import failed'
      };
    }
  }

  // ==================== UTILITY ====================

  async clearProgressData(): Promise<ClearProgressResult> {
    try {
      const sessionRows = this.query<any>('SELECT COUNT(*) as count FROM sessions');
      const performanceRows = this.query<any>('SELECT COUNT(*) as count FROM card_performance');
      const preferencesRows = this.query<any>('SELECT COUNT(*) as count FROM user_card_preferences');

      await this.run('DELETE FROM sessions');
      await this.run('DELETE FROM card_performance');
      await this.run('DELETE FROM user_card_preferences');

      return {
        success: true,
        deletedRecords: {
          sessions: sessionRows[0].count,
          performance: performanceRows[0].count,
          preferences: preferencesRows[0].count
        }
      };
    } catch (error) {
      return {
        success: false,
        deletedRecords: {
          sessions: 0,
          performance: 0,
          preferences: 0
        },
        error: error instanceof Error ? error.message : 'Clear failed'
      };
    }
  }

  async clearDeckProgress(deckId: string): Promise<{ success: boolean; deletedRecords: number; error?: string }> {
    try {
      // Count records before deletion
      const countRows = this.query<{ count: number }>(`
        SELECT COUNT(*) as count
        FROM card_performance cp
        JOIN cards c ON cp.card_id = c.id
        WHERE c.deck_id = ?
      `, [deckId]);
      const recordCount = countRows[0]?.count || 0;

      // Delete performance records for cards in this deck
      await this.run(`
        DELETE FROM card_performance
        WHERE card_id IN (SELECT id FROM cards WHERE deck_id = ?)
      `, [deckId]);

      return {
        success: true,
        deletedRecords: recordCount
      };
    } catch (error) {
      return {
        success: false,
        deletedRecords: 0,
        error: error instanceof Error ? error.message : 'Clear deck progress failed'
      };
    }
  }

  async getCompletedCardIds(deckId: string): Promise<string[]> {
    const rows = this.query<{ card_id: string }>(`
      SELECT DISTINCT cp.card_id
      FROM card_performance cp
      JOIN cards c ON cp.card_id = c.id
      WHERE c.deck_id = ? AND cp.is_correct = 1
    `, [deckId]);

    return rows.map(row => row.card_id);
  }

  async resetCardProgress(cardId: string): Promise<{ success: boolean; deletedRecords: number; error?: string }> {
    try {
      // Count records before deletion
      const countRows = this.query<{ count: number }>(`
        SELECT COUNT(*) as count
        FROM card_performance
        WHERE card_id = ? AND is_correct = 1
      `, [cardId]);
      const recordCount = countRows[0]?.count || 0;

      // Delete correct performance records for this card
      await this.run(`
        DELETE FROM card_performance
        WHERE card_id = ? AND is_correct = 1
      `, [cardId]);

      return {
        success: true,
        deletedRecords: recordCount
      };
    } catch (error) {
      console.error('Failed to reset card progress:', error);
      return {
        success: false,
        deletedRecords: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}
